<template>
  <!--
    เครื่องราชอิสริยาภรณ์ (สถานศึกษา) — ตรวจคุณสมบัติบุคลากรตามระเบียบสำนักนายกรัฐมนตรีฯ พ.ศ. ๒๕๖๔ (บัญชี ๑๕ ๑๖ ๒๕ ๒๖)
    บันทึกประวัติ เสนอรายชื่อ รับรองคุณสมบัติ (ข้อ ๑๒) พิมพ์แบบเสนอขอ และยืนยันส่ง — decoration_college.php
    ผลตรวจคุณสมบัติคำนวณที่เซิร์ฟเวอร์ (decoration_helpers.php) หน้านี้แสดงผลอย่างเดียว
  -->
  <v-container fluid class="text_google">
    <base-material-card icon="mdi-medal" title="เครื่องราชอิสริยาภรณ์ — ตรวจคุณสมบัติและเสนอขอพระราชทาน" class="px-5 py-3">
      <!-- ปีที่ขอ + กำหนดการ -->
      <v-row dense align="center" class="mb-2">
        <v-col cols="12" md="3">
          <v-select v-model="year" :items="yearItems" label="ปีที่ขอพระราชทาน (พ.ศ.)" outlined dense hide-details
            @change="load" />
        </v-col>
        <v-col cols="12" md="9" class="text-md-right">
          <span class="subtitle-1 font-weight-bold">{{ data.college_name }}</span>
          <span class="grey--text ml-2">({{ data.college_code }})</span>
        </v-col>
      </v-row>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

      <template v-if="data.schedule">
        <v-alert v-for="(a, i) in alerts" :key="i" :type="a.type" dense text border="left" class="mb-2">
          <span v-html="a.html" />
        </v-alert>

        <v-row dense class="mb-4">
          <v-col v-for="s in scheduleCards" :key="s.label" cols="6" md="3">
            <div class="sched-box">
              <div class="caption grey--text">{{ s.label }}</div>
              <div class="subtitle-1 font-weight-bold" :class="s.cls">{{ s.value }}</div>
              <div class="caption">{{ s.hint }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- สรุปผลตรวจ (กดเพื่อกรอง) -->
        <div class="d-flex flex-wrap mb-3" style="gap:8px">
          <v-chip v-for="s in summaryChips" :key="s.key" :color="s.color" :outlined="statusFilter !== s.key" label
            :dark="statusFilter === s.key" @click="toggleFilter(s.key)">
            <v-icon left small>{{ s.icon }}</v-icon>{{ s.label }} <b class="ml-1">{{ s.count }}</b>
          </v-chip>
        </div>

        <v-tabs v-model="tab" grow class="mb-2">
          <v-tab><v-icon left>mdi-account-search</v-icon>ตรวจคุณสมบัติบุคลากร ({{ data.persons.length }})</v-tab>
          <v-tab><v-icon left>mdi-clipboard-list</v-icon>รายชื่อที่เสนอขอ ({{ data.requests.length }})</v-tab>
          <v-tab><v-icon left>mdi-book-open-variant</v-icon>เกณฑ์ตามระเบียบ</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- ===== แท็บ 1: ตรวจคุณสมบัติ ===== -->
          <v-tab-item>
            <v-row dense class="my-2">
              <v-col cols="12" md="5">
                <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="ค้นหาชื่อ/ตำแหน่ง" outlined dense
                  hide-details clearable />
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="typeFilter" :items="typeItems" label="ประเภทบุคลากร" outlined dense hide-details clearable />
              </v-col>
              <v-col cols="12" md="3" class="text-right">
                <v-btn outlined color="success" @click="exportExcel"><v-icon left>mdi-microsoft-excel</v-icon>ส่งออก Excel</v-btn>
              </v-col>
            </v-row>
            <v-data-table :headers="headers" :items="filteredPersons" :search="search" item-key="id_card" dense
              show-expand single-expand :items-per-page="25" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
              :custom-filter="personFilter" no-data-text="ไม่มีข้อมูล" class="dec-table">
              <template v-slot:[`item.full_name`]="{ item }">
                <div class="font-weight-medium">{{ item.full_name }}</div>
                <div class="caption grey--text">{{ item.position_name }}{{ item.rang ? " · " + item.rang : "" }}</div>
                <v-chip x-small label class="mt-1">{{ typeLabel(item.person_type) }}</v-chip>
                <v-chip v-if="item.retire_year == year" x-small color="pink" dark class="mt-1 ml-1">เกษียณ {{ year }}</v-chip>
              </template>
              <template v-slot:[`item.category_ref`]="{ item }">
                <template v-if="item.category_key">
                  <div class="caption font-weight-bold">{{ item.category_ref }}</div>
                  <div class="caption">{{ item.category_start || "-" }} → {{ item.category_max }}</div>
                </template>
                <span v-else class="caption grey--text">-</span>
              </template>
              <template v-slot:[`item.highest`]="{ item }">
                <template v-if="item.eval.highest">
                  <b>{{ item.eval.highest.class }}</b>
                  <div class="caption grey--text">{{ thaiDate(item.eval.highest.date) }}</div>
                </template>
                <span v-else class="caption grey--text">{{ item.never_received ? "ไม่เคยได้รับ (ยืนยันแล้ว)" : "ไม่มีประวัติ" }}</span>
              </template>
              <template v-slot:[`item.next`]="{ item }">
                <template v-if="item.eval.next_class">
                  <b class="primary--text">{{ item.eval.next_class }}</b>
                  <div class="caption">{{ className(item.eval.next_class) }}</div>
                </template>
                <span v-else>-</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="STATUS[item.eval.status].color" dark>
                  <v-icon left small>{{ STATUS[item.eval.status].icon }}</v-icon>{{ STATUS[item.eval.status].label }}
                </v-chip>
                <div v-if="item.eval.earliest_year" class="caption">ขอได้ปี {{ item.eval.earliest_year }}</div>
                <div v-if="item.eval.reason" class="caption grey--text text--darken-1 reason">{{ item.eval.reason }}</div>
                <div v-if="item.eval.warnings.length" class="caption orange--text text--darken-3">
                  <v-icon x-small color="orange darken-3">mdi-alert</v-icon> ข้อสังเกต {{ item.eval.warnings.length }} รายการ
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center" style="gap:4px">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small color="indigo" v-on="on" @click="openHistory(item)"><v-icon small>mdi-medal-outline</v-icon></v-btn>
                    </template>
                    <span>ประวัติเครื่องราชฯ ({{ item.decorations.length }})</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small color="teal" v-on="on" @click="openInfo(item)"><v-icon small>mdi-calendar-edit</v-icon></v-btn>
                    </template>
                    <span>ข้อมูลประกอบการตรวจ (วันเริ่มรับราชการ/ดำรงตำแหน่ง)</span>
                  </v-tooltip>
                  <v-chip v-if="requestOf(item)" x-small color="success" dark label>เสนอแล้ว</v-chip>
                  <v-btn v-else-if="canRequest(item)" x-small depressed color="primary" :disabled="locked" @click="openRequest(item)">
                    เสนอขอ
                  </v-btn>
                </div>
              </template>
              <template v-slot:expanded-item="{ headers: h, item }">
                <td :colspan="h.length" class="py-3 grey lighten-5">
                  <v-row dense>
                    <v-col cols="12" md="7">
                      <div class="caption font-weight-bold mb-1">ผลตรวจรายเงื่อนไข — ขอ {{ item.eval.next_class || "-" }}</div>
                      <div v-for="(c, i) in item.eval.checks" :key="i" class="caption">
                        <v-icon x-small :color="c.pass === true ? 'success' : c.pass === false ? 'error' : 'amber darken-2'">
                          {{ c.pass === true ? "mdi-check-circle" : c.pass === false ? "mdi-close-circle" : "mdi-help-circle" }}
                        </v-icon>
                        {{ c.label }} — {{ c.detail }}
                      </div>
                      <div v-if="!item.eval.checks.length" class="caption grey--text">{{ item.eval.reason || "-" }}</div>
                      <div v-for="(w, i) in item.eval.warnings" :key="'w' + i" class="caption orange--text text--darken-3">
                        <v-icon x-small color="orange darken-3">mdi-alert</v-icon> {{ w }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="5" class="caption">
                      <div class="font-weight-bold mb-1">ข้อมูลที่ใช้ตรวจ</div>
                      <div>วันเริ่มรับราชการ/เริ่มจ้าง: {{ thaiDate(item.start_date) }}
                        <span class="grey--text">({{ item.start_date_source || "ไม่มี" }})</span></div>
                      <div>ดำรงตำแหน่ง/ระดับปัจจุบัน: {{ thaiDate(item.position_date) }}
                        <span class="grey--text">({{ item.position_date_source || "ไม่มี" }})</span></div>
                      <div>เงินเดือน/ค่าตอบแทน: {{ money(item.salary_s) }} บาท · เกษียณ พ.ศ. {{ item.retire_year || "-" }}</div>
                      <div v-if="item.category_label">เกณฑ์: {{ item.category_ref }} {{ item.category_label }}</div>
                      <div class="mt-1">ประวัติ:
                        <span v-for="(d, i) in item.decorations" :key="i" class="mr-2">
                          <b :class="d.class ? '' : 'error--text'">{{ d.class || d.text }}</b> {{ thaiDate(d.date) }}
                        </span>
                        <span v-if="!item.decorations.length" class="grey--text">-</span>
                      </div>
                    </v-col>
                  </v-row>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ===== แท็บ 2: รายชื่อที่เสนอขอ ===== -->
          <v-tab-item>
            <div class="d-flex flex-wrap align-center my-3" style="gap:8px">
              <v-btn outlined color="primary" :disabled="!data.requests.length" @click="printList">
                <v-icon left>mdi-printer</v-icon>บัญชีแสดงคุณสมบัติ
              </v-btn>
              <v-btn outlined color="primary" :disabled="!data.requests.length" @click="printSummary">
                <v-icon left>mdi-table</v-icon>บัญชีจำนวนชั้นตรา
              </v-btn>
              <v-btn outlined color="primary" :disabled="!data.requests.length" @click="printCertify">
                <v-icon left>mdi-file-sign</v-icon>แบบรับรองคุณสมบัติตนเอง
              </v-btn>
              <v-spacer />
              <template v-if="locked">
                <v-chip color="success" dark label class="mr-2">
                  <v-icon left>mdi-check-all</v-icon>
                  {{ data.submission.status === "accepted" ? "ส่วนกลางรับรายชื่อแล้ว" : "ยืนยันส่งแล้ว" }}
                  {{ thaiDate(data.submission.submitted_at) }}{{ data.submission.doc_no ? " · " + data.submission.doc_no : "" }}
                </v-chip>
                <v-btn v-if="data.submission.status === 'submitted'" outlined color="error" :loading="busy" @click="unsubmit">
                  ยกเลิกการยืนยัน
                </v-btn>
              </template>
              <v-btn v-else color="success" depressed :loading="busy" @click="submitDialog = true">
                <v-icon left>mdi-send-check</v-icon>ยืนยันส่งรายชื่อ ({{ data.requests.length }})
              </v-btn>
            </div>
            <v-alert v-if="data.submission && data.submission.status === 'returned'" type="error" dense text>
              ส่วนกลางส่งกลับให้แก้ไข: {{ data.submission.returned_note || "-" }}
            </v-alert>
            <v-data-table :headers="requestHeaders" :items="data.requests" dense item-key="id" :items-per-page="-1"
              hide-default-footer no-data-text="ยังไม่มีรายชื่อ — เลือก “เสนอขอ” ที่แท็บตรวจคุณสมบัติ">
              <template v-slot:[`item.name`]="{ item }">
                <div class="font-weight-medium">{{ item.snapshot.full_name }}</div>
                <div class="caption grey--text">{{ item.snapshot.position_name }} · {{ typeLabel(item.person_type) }}</div>
              </template>
              <template v-slot:[`item.request_class`]="{ item }">
                <b class="primary--text">{{ item.request_class }}</b>
                <div class="caption">{{ className(item.request_class) }}</div>
                <v-chip v-if="isSash(item.request_class)" x-small color="deep-purple" dark>สายสะพาย</v-chip>
              </template>
              <template v-slot:[`item.last_class`]="{ item }">
                {{ item.last_class || "-" }}<div class="caption grey--text">{{ thaiDate(item.last_class_date) }}</div>
              </template>
              <template v-slot:[`item.certify`]="{ item }">
                <v-chip small :color="certifyComplete(item.certify) ? 'success' : 'warning'" dark>
                  {{ certifyCount(item.certify) }}/{{ CERTIFY_ITEMS.length }}
                </v-chip>
                <v-chip v-if="item.eligible_status === 'review'" x-small color="amber darken-2" dark class="ml-1">ต้องตรวจสอบ</v-chip>
              </template>
              <template v-slot:[`item.note`]="{ item }"><span class="caption">{{ item.note || "-" }}</span></template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon small color="warning" :disabled="locked" @click="openRequest(personOf(item), item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="error" :disabled="locked" @click="removeRequest(item)"><v-icon small>mdi-delete</v-icon></v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ===== แท็บ 3: เกณฑ์ ===== -->
          <v-tab-item>
            <v-alert type="info" dense text class="mt-3 caption">
              สรุปจากระเบียบสำนักนายกรัฐมนตรีว่าด้วยการขอพระราชทานเครื่องราชอิสริยาภรณ์ฯ พ.ศ. ๒๕๖๔ — ระยะเวลารับราชการ/ปฏิบัติงานนับถึงวันก่อนวันพระราชพิธี
              ไม่น้อยกว่า ๖๐ วัน ({{ thaiDate(data.schedule.service_cutoff) }}) · ขอปีติดกันไม่ได้ (ข้อ ๑๑) เว้นแต่บัญชีกำหนด “ทุกปี”
              · ถูกลงโทษทางวินัย/ผลประเมินต่ำกว่าระดับดีในปีใด เพิ่มระยะเวลากรณีละ ๑ ปี (ข้อ ๑๘)
            </v-alert>
            <v-data-table :headers="categoryHeaders" :items="data.categories" dense hide-default-footer :items-per-page="-1">
              <template v-slot:[`item.type`]="{ item }">{{ typeLabel(item.type) }}</template>
              <template v-slot:[`item.steps`]="{ item }">
                <div v-for="(s, i) in item.steps" :key="i" class="caption">{{ s }}</div>
                <span v-if="!item.steps.length" class="caption grey--text">ไม่มีการเลื่อนชั้นตรา</span>
              </template>
            </v-data-table>
            <div class="subtitle-2 font-weight-bold mt-4 mb-1">เกณฑ์เงินเดือนที่ใช้ตรวจ</div>
            <v-simple-table dense>
              <tbody>
                <tr v-for="(s, key) in data.settings" :key="key">
                  <td class="caption">{{ s.label }} <span class="grey--text">[{{ key }}]</span></td>
                  <td class="text-right" style="width:220px">
                    <v-text-field v-if="data.can_edit_settings" v-model.number="settingEdit[key]" type="number" dense hide-details
                      suffix="บาท" append-outer-icon="mdi-content-save" @click:append-outer="saveSetting(key)" />
                    <b v-else>{{ money(s.value) }}</b>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="caption grey--text mt-1">ตรวจสอบกับบัญชีอัตราเงินเดือนที่ใช้บังคับปัจจุบัน — ส่วนกลางเป็นผู้ปรับเกณฑ์</div>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </base-material-card>

    <!-- ประวัติเครื่องราชฯ (ใช้ component เดียวกับหน้าบุคลากร /personnel_decoration) -->
    <v-dialog v-model="historyDialog" max-width="1100" scrollable>
      <v-card v-if="historyPerson">
        <v-toolbar dense dark color="indigo" flat class="flex-grow-0">
          <v-icon left>mdi-medal-outline</v-icon>
          <v-toolbar-title class="subtitle-1">ประวัติเครื่องราชอิสริยาภรณ์ — {{ historyPerson.full_name }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="historyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <Personnel_decoration :key="historyPerson.id_card" compact :editpersonnel_temporary="{ id_card: historyPerson.id_card }"
            @changed="historyChanged = true" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ข้อมูลประกอบการตรวจ -->
    <v-dialog v-model="infoDialog" max-width="640" persistent>
      <v-card v-if="infoPerson">
        <v-toolbar dense dark color="teal" flat>
          <v-toolbar-title class="subtitle-1">ข้อมูลประกอบการตรวจ — {{ infoPerson.full_name }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="infoDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="caption">
            ใช้เมื่อทะเบียนประวัติไม่มีข้อมูลหรือข้อมูลไม่ถูกต้อง — ไม่แก้ทะเบียนประวัติ ใช้เฉพาะการตรวจคุณสมบัติเครื่องราชฯ
          </v-alert>
          <v-text-field v-model="infoForm.start_date" type="date" outlined dense label="วันเริ่มรับราชการ / เริ่มจ้าง"
            :hint="'ทะเบียนประวัติ: ' + thaiDate(infoPerson.start_date)" persistent-hint class="mb-3" />
          <v-text-field v-model="infoForm.position_date" type="date" outlined dense label="วันที่ดำรงตำแหน่ง/ระดับปัจจุบัน"
            hint="เกณฑ์ “ดำรงตำแหน่งมาแล้วไม่น้อยกว่า ... ปี” (บัญชี ๑๕)" persistent-hint class="mb-3" />
          <v-text-field v-model="infoForm.salary_min_date" type="date" outlined dense
            label="วันที่ได้รับเงินเดือนถึงขั้นต่ำ คศ.๓ / ชำนาญการพิเศษ"
            hint="เกณฑ์ ท.ช. ของวิทยฐานะชำนาญการ (เงินเดือนถึงขั้นต่ำมาแล้ว ๕ ปี)" persistent-hint class="mb-3" />
          <v-checkbox v-model="infoForm.never_received" dense hide-details
            label="ตรวจสอบแล้ว ไม่เคยได้รับพระราชทานเครื่องราชอิสริยาภรณ์" />
          <v-text-field v-model="infoForm.note" outlined dense label="หมายเหตุ/แหล่งที่มาของข้อมูล" class="mt-3" counter="500" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="infoDialog = false">ยกเลิก</v-btn>
          <v-btn color="teal" dark :loading="busy" @click="saveInfo">บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- เสนอขอ + รับรองคุณสมบัติ -->
    <v-dialog v-model="requestDialog" max-width="760" persistent scrollable>
      <v-card v-if="requestPerson">
        <v-toolbar dense dark color="primary" flat class="flex-grow-0">
          <v-toolbar-title class="subtitle-1">เสนอขอพระราชทาน — {{ requestPerson.full_name }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="requestDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <div class="primary--text font-weight-bold" style="font-size:20px">
            {{ requestPerson.eval.next_class }} — {{ className(requestPerson.eval.next_class) }}
          </div>
          <div class="caption mb-2">{{ orderName(requestPerson.eval.next_class) }} · {{ requestPerson.category_ref }}
            {{ requestPerson.category_label }}</div>
          <div v-for="(c, i) in requestPerson.eval.checks" :key="i" class="caption">
            <v-icon x-small :color="c.pass === true ? 'success' : c.pass === false ? 'error' : 'amber darken-2'">
              {{ c.pass === true ? "mdi-check-circle" : "mdi-help-circle" }}
            </v-icon>
            {{ c.label }} — {{ c.detail }}
          </div>
          <div v-for="(w, i) in requestPerson.eval.warnings" :key="'w' + i" class="caption orange--text text--darken-3">
            <v-icon x-small color="orange darken-3">mdi-alert</v-icon> {{ w }}
          </div>
          <v-divider class="my-3" />
          <div class="subtitle-2 font-weight-bold">รับรองคุณสมบัติและลักษณะต้องห้าม (ข้อ ๑๒)</div>
          <div class="caption grey--text mb-1">ตรวจจากเอกสาร/แบบรับรองตนเองที่บุคคลลงนามแล้ว — ต้องครบทุกข้อจึงยืนยันส่งได้</div>
          <v-checkbox v-for="i in CERTIFY_ITEMS" :key="i.key" v-model="requestForm.certify[i.key]" dense hide-details class="mt-1">
            <template v-slot:label><span class="body-2">{{ i.text }} <span class="caption grey--text">({{ i.ref }})</span></span></template>
          </v-checkbox>
          <v-btn x-small text color="primary" class="mt-1" @click="checkAll">เลือกทั้งหมด</v-btn>
          <v-textarea v-model="requestForm.note" outlined dense rows="2" class="mt-3" counter="500"
            :label="requestPerson.eval.status === 'review' ? 'หมายเหตุประกอบการเสนอ (จำเป็น — ผลตรวจยังมีรายการที่ต้องตรวจสอบ)' : 'หมายเหตุ'" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="requestDialog = false">ยกเลิก</v-btn>
          <v-btn color="primary" :loading="busy" @click="saveRequest">บันทึกรายชื่อ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ยืนยันส่ง -->
    <v-dialog v-model="submitDialog" max-width="560">
      <v-card>
        <v-toolbar dense dark color="success" flat><v-toolbar-title class="subtitle-1">ยืนยันส่งรายชื่อ ปี {{ year }}</v-toolbar-title></v-toolbar>
        <v-card-text class="pt-4">
          <v-alert :type="data.requests.length ? 'info' : 'warning'" dense text>
            {{ data.requests.length ? `เสนอขอ ${data.requests.length} ราย — หลังยืนยันจะแก้ไขรายชื่อไม่ได้จนกว่าจะยกเลิกการยืนยัน`
              : "ยืนยันว่าปีนี้ไม่มีผู้เสนอขอพระราชทานเครื่องราชอิสริยาภรณ์" }}
          </v-alert>
          <v-text-field v-model="submitForm.doc_no" outlined dense label="เลขที่หนังสือนำส่ง (ถ้ามี)" />
          <v-text-field v-model="submitForm.note" outlined dense label="หมายเหตุ" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="submitDialog = false">ยกเลิก</v-btn>
          <v-btn color="success" :loading="busy" @click="submit">ยืนยันส่ง</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="6000">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import Personnel_decoration from "@/views/dashboard/components/college/personnel_decoration.vue";
import { CERTIFY_ITEMS, STATUS, certifyComplete, className, isSash, orderName, thaiDate } from "@/utils/decorationClasses";
import { printClassSummary, printQualificationList, printSelfCertify } from "@/utils/decorationPrint";

const API = "decoration_college.php";

export default {
  name: "CollegeDecoration",
  components: { Personnel_decoration },
  data() {
    return {
      STATUS,
      CERTIFY_ITEMS,
      year: null,
      loading: false,
      busy: false,
      tab: 0,
      search: "",
      typeFilter: null,
      statusFilter: null,
      data: { persons: [], requests: [], categories: [], settings: {}, years: [], type_labels: {} },
      settingEdit: {},
      historyDialog: false,
      historyPerson: null,
      historyChanged: false,
      infoDialog: false,
      infoPerson: null,
      infoForm: {},
      requestDialog: false,
      requestPerson: null,
      requestForm: { certify: {}, note: "" },
      submitDialog: false,
      submitForm: { doc_no: "", note: "" },
      snack: { show: false, text: "", color: "success" },
      headers: [
        { text: "ชื่อ-สกุล / ตำแหน่ง", value: "full_name" },
        { text: "เกณฑ์", value: "category_ref", width: 150 },
        { text: "ชั้นตราสูงสุดที่ได้รับ", value: "highest", sortable: false, width: 130 },
        { text: "ขอครั้งถัดไป", value: "next", sortable: false, width: 150 },
        { text: "ผลตรวจปีนี้", value: "status", sortable: false, width: 240 },
        { text: "", value: "actions", sortable: false, width: 150 },
        { text: "", value: "data-table-expand" }
      ],
      requestHeaders: [
        { text: "ชื่อ-สกุล", value: "name", sortable: false },
        { text: "ชั้นตราที่ขอ", value: "request_class", width: 180 },
        { text: "ชั้นตราเดิม", value: "last_class", width: 120 },
        { text: "รับรองคุณสมบัติ", value: "certify", sortable: false, width: 170 },
        { text: "หมายเหตุ", value: "note", sortable: false },
        { text: "", value: "actions", sortable: false, width: 100 }
      ],
      categoryHeaders: [
        { text: "บัญชี", value: "ref", width: 140 },
        { text: "ประเภท", value: "type", width: 170 },
        { text: "ตำแหน่ง/ระดับ", value: "label" },
        { text: "ระยะเวลาขั้นต่ำ (ปี)", value: "service", align: "center", width: 110 },
        { text: "เริ่มต้นขอ", value: "start", align: "center", width: 90 },
        { text: "เลื่อนได้ถึง", value: "max", align: "center", width: 90 },
        { text: "การเลื่อนชั้นตรา", value: "steps", sortable: false }
      ]
    };
  },
  computed: {
    yearItems() {
      return (this.data.years || []).map(y => ({ text: `${y} (พระราชพิธี 28 ก.ค. ${y})`, value: y }));
    },
    typeItems() {
      return Object.entries(this.data.type_labels || {}).map(([value, text]) => ({ value, text }));
    },
    locked() {
      return !!(this.data.submission && ["submitted", "accepted"].includes(this.data.submission.status));
    },
    filteredPersons() {
      return this.data.persons.filter(
        p =>
          (!this.typeFilter || p.person_type === this.typeFilter) &&
          (!this.statusFilter ||
            (this.statusFilter === "requested" ? !!this.requestOf(p) : p.eval.status === this.statusFilter))
      );
    },
    summaryChips() {
      const s = this.data.summary || {};
      return [
        ...["eligible", "review", "not_yet", "need_data", "max_reached", "not_covered"].map(k => ({
          key: k, count: s[k] || 0, ...STATUS[k]
        })),
        { key: "requested", count: s.requested || 0, label: "เสนอขอแล้ว", color: "primary", icon: "mdi-clipboard-check" }
      ];
    },
    scheduleCards() {
      const sc = this.data.schedule;
      const p = this.data.period;
      return [
        { label: "วันพระราชพิธีเฉลิมพระชนมพรรษา", value: thaiDate(sc.ceremony_date), hint: "ปีที่ขอพระราชทาน " + sc.year },
        {
          label: "สถานศึกษาส่งรายชื่อ (กำหนดส่วนกลาง)",
          value: p ? `${thaiDate(p.start)} – ${thaiDate(p.stop)}` : "ยังไม่กำหนด",
          hint: p ? { open: `เปิดอยู่ · เหลือ ${p.days_left} วัน`, upcoming: "ยังไม่เปิด", ended: "สิ้นสุดแล้ว", closed: "ปิดอยู่" }[p.state] : "เตรียมข้อมูลได้ตลอด",
          cls: p && p.state === "open" && p.days_left <= 7 ? "error--text" : ""
        },
        { label: "ส่ง สลค. ไม่น้อยกว่า ๙๐ วันก่อนวันพระราชพิธี (ข้อ ๑๓)", value: thaiDate(sc.soc_deadline), hint: "ส่วนกลางรวบรวมส่ง" },
        { label: "วันตัดนับระยะเวลารับราชการ (ก่อนพิธี ๖๐ วัน)", value: thaiDate(sc.service_cutoff), hint: "ใช้ตรวจ ๕ ปี / ๘ ปีบริบูรณ์" }
      ];
    },
    alerts() {
      const out = [];
      const p = this.data.period;
      const s = this.data.summary || {};
      const sub = this.data.submission;
      if (sub && sub.status === "returned") {
        out.push({ type: "error", html: `ส่วนกลางส่งกลับให้แก้ไข: <b>${this.escape(sub.returned_note || "-")}</b> — แก้ไขแล้วยืนยันส่งอีกครั้ง` });
      }
      if (this.locked) {
        out.push({
          type: "success",
          html: `${sub.status === "accepted" ? "ส่วนกลางรับรายชื่อ" : "ยืนยันส่งรายชื่อ"}ปี ${this.year} แล้ว <b>${sub.request_count} ราย</b>`
        });
      } else if (p && p.state === "open") {
        out.push({
          type: p.days_left <= 7 ? "error" : "warning",
          html: `เปิดให้สถานศึกษาเสนอรายชื่อถึง <b>${thaiDate(p.stop)}</b> (เหลือ ${p.days_left} วัน) — ครบเกณฑ์ ${s.eligible || 0} ราย ` +
            `ต้องตรวจสอบ ${s.review || 0} ราย · บันทึกแล้ว ${this.data.requests.length} ราย ยังไม่ยืนยันส่ง`
        });
      } else if (p && p.state === "upcoming") {
        out.push({ type: "info", html: `จะเปิดให้เสนอรายชื่อ ${thaiDate(p.start)} – ${thaiDate(p.stop)} — ตรวจและบันทึกประวัติเครื่องราชฯ ให้ครบก่อน` });
      } else if (!p) {
        out.push({ type: "info", html: "ส่วนกลางยังไม่กำหนดวันส่งรายชื่อของปีนี้ — ตรวจคุณสมบัติและบันทึกประวัติเครื่องราชฯ เตรียมไว้ได้" });
      }
      if (s.need_data) out.push({ type: "warning", html: `ข้อมูลไม่พอสำหรับตรวจ <b>${s.need_data} ราย</b> — กรอก “ข้อมูลประกอบการตรวจ” (ไอคอนปฏิทิน)` });
      return out;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    className,
    orderName,
    isSash,
    thaiDate,
    certifyComplete,
    money(v) {
      return v ? Number(v).toLocaleString("th-TH") : "-";
    },
    escape(s) {
      return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },
    typeLabel(t) {
      return (this.data.type_labels || {})[t] || t;
    },
    certifyCount(c) {
      return CERTIFY_ITEMS.filter(i => c && c[i.key]).length;
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    async api(payload) {
      const extra = this.$route.query.college_code ? { college_code: this.$route.query.college_code } : {};
      try {
        const res = await this.$http.post(API, { ApiKey: "HRvec2021", year: this.year, ...extra, ...payload });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    async load() {
      this.loading = true;
      try {
        const d = await this.api({ action: "overview" });
        this.data = d;
        this.year = d.year;
        this.settingEdit = Object.fromEntries(Object.entries(d.settings || {}).map(([k, s]) => [k, s.value]));
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.loading = false;
      }
    },
    toggleFilter(key) {
      this.statusFilter = this.statusFilter === key ? null : key;
      if (key === "requested" && this.statusFilter) this.tab = 0;
    },
    personFilter(value, search, item) {
      const q = String(search || "").toLowerCase();
      return [item.full_name, item.position_name, item.rang, item.id_card].some(v => String(v || "").toLowerCase().includes(q));
    },
    requestOf(p) {
      return this.data.requests.find(r => r.id_card === p.id_card);
    },
    personOf(r) {
      return this.data.persons.find(p => p.id_card === r.id_card) || null;
    },
    canRequest(p) {
      return ["eligible", "review"].includes(p.eval.status) && !!p.eval.next_class;
    },
    openHistory(p) {
      this.historyPerson = p;
      this.historyChanged = false;
      this.historyDialog = true;
    },
    openInfo(p) {
      this.infoPerson = p;
      this.infoForm = {
        start_date: p.start_date_source === "กรอกเพิ่ม" ? p.start_date : "",
        position_date: p.position_date_source === "กรอกเพิ่ม" ? p.position_date : "",
        salary_min_date: p.salary_min_date || "",
        never_received: !!p.never_received,
        note: p.info_note || ""
      };
      this.infoDialog = true;
    },
    async saveInfo() {
      this.busy = true;
      try {
        await this.api({ action: "save_info", id_card: this.infoPerson.id_card, ...this.infoForm });
        this.infoDialog = false;
        this.notify("บันทึกข้อมูลประกอบการตรวจแล้ว");
        await this.load();
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    openRequest(p, existing) {
      if (!p) {
        this.notify("ไม่พบบุคคลนี้ในสถานศึกษาแล้ว — ลบรายการออก", "error");
        return;
      }
      const r = existing || this.requestOf(p);
      this.requestPerson = p;
      this.requestForm = { certify: { ...((r && r.certify) || {}) }, note: (r && r.note) || "" };
      this.requestDialog = true;
    },
    checkAll() {
      CERTIFY_ITEMS.forEach(i => this.$set(this.requestForm.certify, i.key, true));
    },
    async saveRequest() {
      this.busy = true;
      try {
        const d = await this.api({ action: "save_request", id_card: this.requestPerson.id_card, ...this.requestForm });
        this.data.requests = d.requests;
        this.data.summary.requested = d.requests.length;
        this.requestDialog = false;
        this.notify(`บันทึกรายชื่อ ${this.requestPerson.full_name} แล้ว`);
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    async removeRequest(r) {
      if (!confirm(`นำ ${r.snapshot.full_name} ออกจากรายชื่อที่เสนอขอ?`)) return;
      try {
        const d = await this.api({ action: "delete_request", id_card: r.id_card });
        this.data.requests = d.requests;
        this.data.summary.requested = d.requests.length;
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    async submit() {
      this.busy = true;
      try {
        const d = await this.api({ action: "submit", ...this.submitForm });
        this.data.submission = d.submission;
        this.submitDialog = false;
        this.notify("ยืนยันส่งรายชื่อแล้ว");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    async unsubmit() {
      if (!confirm("ยกเลิกการยืนยันส่งรายชื่อ เพื่อแก้ไขรายการ?")) return;
      this.busy = true;
      try {
        await this.api({ action: "unsubmit" });
        this.data.submission = null;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    async saveSetting(key) {
      try {
        const d = await this.api({ action: "save_setting", key, value: this.settingEdit[key] });
        this.data.settings = d.settings;
        this.notify("บันทึกเกณฑ์แล้ว — โหลดผลตรวจใหม่");
        await this.load();
      } catch (e) {
        this.notify(e.message, "error");
      }
    },
    ctx() {
      return { year: this.year, collegeName: this.data.college_name };
    },
    printList() {
      printQualificationList(this.data.requests, this.ctx());
    },
    printSummary() {
      printClassSummary(this.data.requests, this.ctx());
    },
    printCertify() {
      printSelfCertify(this.data.requests, this.ctx());
    },
    exportExcel() {
      const rows = this.data.persons.map((p, i) => ({
        ลำดับ: i + 1,
        เลขบัตรประชาชน: p.id_card,
        "ชื่อ-สกุล": p.full_name,
        ประเภท: this.typeLabel(p.person_type),
        ตำแหน่ง: p.position_name,
        "ระดับ/วิทยฐานะ": p.rang,
        เงินเดือน: p.salary_s,
        วันเริ่มรับราชการ: thaiDate(p.start_date),
        เกณฑ์: p.category_ref ? `${p.category_ref} ${p.category_label}` : "",
        "ประวัติเครื่องราชฯ": p.decorations.map(d => `${d.class || d.text} ${thaiDate(d.date)}`).join(", "),
        ขอครั้งถัดไป: p.eval.next_class || "",
        ผลตรวจ: STATUS[p.eval.status].label,
        ขอได้ปี: p.eval.earliest_year || "",
        เหตุผล: p.eval.reason || "",
        ข้อสังเกต: p.eval.warnings.join(" | "),
        เสนอขอแล้ว: this.requestOf(p) ? "ใช่" : ""
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "ตรวจคุณสมบัติ");
      XLSX.writeFile(wb, `เครื่องราชฯ_${this.data.college_code}_${this.year}.xlsx`);
    }
  },
  watch: {
    historyDialog(open) {
      if (!open && this.historyChanged) this.load();
    }
  }
};
</script>

<style scoped>
.dec-table >>> td {
  vertical-align: top;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
.reason {
  max-width: 260px;
  white-space: normal;
}
.sched-box {
  height: 100%;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
}
</style>
